import ToastificationContent from "@core/components/toastification/ToastificationContent";
import {ProductProvider, clearCachedProduct} from "@core/domain/product";
import {i18n} from "@/libs/i18n";
import {getAllSubstanceLists} from "@core/domain/substance";
import backendResponse from "@core/mixins/ui/backend-response";
import {ProductType} from "@/libs/product";
import {LegislationApi} from "@core/api";
import {LegislationKey} from "@core/domain/legislation";

let productState;

export default {
    data: {
        substanceLists: [],
        product: {
            type: '',
            names: [],
            identifiers: [],
            businessRoles: [],
            comment: '',
            productGroups: [],
            includedProducts: [],
            eventStatuses: [],
            categories: [],
            hasConcernSubstances: null,
            countryOfOrigin: null,
            canDelete: false,
            informationOwnerSettings: {
                weHaveInformation: null,
                supplierHasInformation: null
            },
            legislations: {},
            hasBeenScipNotified: null,
            submissionDetails: null,
            isPutOnTheMarket: null,
            isTopLevel: null,
            regulatedProductStatements: [],
            legislationQuestionAnswers: [],
        },
        scipNotificationFacts: {
            isNotificationGranted: false,
            isSubscriberProperlyConfigured: false,
            productDecision: {
                canBeNotified: false,
                hasChangesToBeReported: false,
                hasSubstancesAboveSpecifiedConcentrationRange: false,
                hasScipNumbersOnComponentLevel: false,
                hasScipNumberReference: false,
            },
            sandBoxSettings: null
        },
        productDescription: {file: null},
        includedSubstances: [],
        excludedSubstances: [],
        evidenceReports: [],
        otherVerificationDocuments: [],
        declarationsOfConformity: [],
        compliantStatement: [],
        safeUseInstructions: {
            description: '',
            file: null
        },
        disassemblingInstructions: {
            description: '',
            file: null
        },
        safeUseApplicable: null,
        needToSaveSubstances: false,

        suppliers: [],
        legislations: [],

        fmdDeclarations: [],
        loadingFMDDeclarations: false,

        isScipEnabled: true,
        isRohsEnabled: true,
        statusExplanationRevision: 1
    },
    methods: {
        reInitData() {
            this.substanceList = null;
            this.product = {
                type: '',
                names: [],
                identifiers: [],
                businessRoles: [],
                comment: '',
                productGroups: [],
                includedProducts: [],
                eventStatuses: [],
                categories: [],
                characteristics: {},
                countryOfOrigin: null,
                hasConcernSubstances: null,
                canDelete: false,
                informationOwnerSettings: {
                    weHaveInformation: null,
                    supplierHasInformation: null
                },
                isPutOnTheMarket: null,
                substanceListQuestionStates: [],
                regulatedProductStatements: [],
                legislationQuestionAnswers: [],
            };
            this.scipNotificationFacts = {
                isNotificationGranted: false,
                isSubscriberProperlyConfigured: false,
                productDecision: {
                    hasChangesToBeReported: false,
                    hasSubstancesAboveSpecifiedConcentrationRange: false,
                    hasScipNumbersOnComponentLevel: false
                },
                sandBoxSettings: null
            };
            this.fmdDeclarations = [];
            this.includedSubstances = [];
            this.excludedSubstances = [];
            this.evidenceReports = [];
            this.otherVerificationDocuments = [];
            this.declarationsOfConformity = [];
            this.compliantStatement = [];
            this.safeUseInstructions = {
                description: '',
                file: null
            };
            this.disassemblingInstructions = {
                description: '',
                file: null
            };
            this.safeUseApplicable = null;
            this.needToSaveSubstances = false;
            this.suppliers = [];
            this.isScipEnabled = true;
            this.isRohsEnabled = true;
        },
        getProductId() {
            throw new Error('getProductId method has to be implemented by the client');
        },
        informationOwnerSettingsChanged(settings) {
            const product = this.getProduct();

            if (product.isInited()) {
                product.setInformationOwnerSettings(settings);
                return;
            }

            product
                .getCard()
                .then(() => {
                    product.setInformationOwnerSettings(settings);
                });
        },
        updateLegislationSettings({key, settings}) {
            const product = this.getProduct();
            product
                .getCard()
                .then(() => {
                    product.setLegislationSettings(key, settings);
                });
        },
        suppliersChange(idList, suppliers) {
            this.suppliers = suppliers;
            const product = this.getProduct();
            product
                .getCard()
                .then(() => {
                    product.setSuppliers(suppliers);
                });
        },
        onFMDChange(change) {
            this.fmdDeclarations = change.declarations;
            this.getProduct().setFMDDeclarations(change.declarations);
        },
        onLegislationQuestionAnswersChange(answers) {
            if (Array.isArray(this.legislationQuestionAnswers)) {
                this.legislationQuestionAnswers = answers;
            }
            this.getProduct().setLegislationQuestionAnswers(answers);
        },
        setSuppliers(suppliers) {
            this.suppliers = suppliers;
        },
        getProduct() {
            return ProductProvider(this.getProductId());
        },
        refreshProduct() {
            return ProductProvider(this.getProductId())
                .refreshCard()
                .then(card => {
                    this.initProduct(card);
                })
            ;
        },
        init() {
            this.reInitData();
            getAllSubstanceLists().then(lists => this.substanceLists = lists);
            const legislationApiPromise = LegislationApi.all().then(r => {
                this.legislations = r.data;

                if (!this.legislations.find(l => l.key === LegislationKey.SCIP)) {
                    this.isScipEnabled = false;
                }
                if (!this.legislations.find(l => l.key === LegislationKey.ROHS)) {
                    this.isRohsEnabled = false;
                }
            });

            let product = this.getProduct();
            product
                .getCard()
                .then((card) => {
                    this.initProduct(card);
                    setTimeout(() => this.showLoading = false, 400);
                })
                .then(() => {
                    setTimeout(() => {
                        legislationApiPromise.then(() => {
                            this.initScipNotificationFacts(product);
                        });
                        this.fetchSubstancesAndDocuments();
                    }, 600);
                })
        },

        initScipNotificationFacts(product) {
            if (this.isScipEnabled) {
                product
                    .getSCIPNotificationFacts()
                    .then(facts => {
                        this.scipNotificationFacts = facts;
                    });
            }
        },

        initProduct(product) {
            this.product = product;

            this.safeUseApplicable = product.isSafeUseApplicable;
            if (this.product.suppliers) {
                this.suppliers = this.product.suppliers;
            }

            this.updateProductState();
            this.initProductBreadcrumbs();
        },

        initProductBreadcrumbs() {},

        makeProductState() {
            return ProductProvider(this.getProductId()).getState();
        },

        hasUnSavedChanges() {
            return JSON.stringify(productState) !== JSON.stringify(this.makeProductState());
        },

        deleteDocument(document) {
            return new Promise((resolve, reject) => {
                this.$swal({
                    title: i18n.t('Component(DeleteDocumentPrompt):title'),
                    text: i18n.t('Component(DeleteDocumentPrompt):text'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: i18n.t('Component(DeleteDocumentPrompt):button-delete'),
                    cancelButtonText: i18n.t('Component(Button):cancel'),
                    customClass: {
                        confirmButton: 'btn btn-danger',
                        cancelButton: 'btn btn-outline-secondary ml-1',
                    },
                    buttonsStyling: false,
                }).then(result => {
                    if (result.isConfirmed) {
                        ProductProvider(this.getProductId())
                            .deleteDocument(document)
                            .then(() => {
                                resolve();
                                this.$swal({
                                    icon: 'success',
                                    title: i18n.t('Component(DeleteDocumentAck):title'),
                                    text: i18n.t('Component(DeleteDocumentAck):text'),
                                    customClass: {
                                        confirmButton: 'btn btn-primary',
                                    },
                                })
                            })
                            .catch((error) => {
                                let errorMessage = 'Some error occurred, please contact your administrator';
                                if (error.response) {
                                    errorMessage = error.response.data.title || errorMessage;
                                } else if (error.request) {
                                    // The request was made but no response was received
                                    console.log(error.request);
                                } else {
                                    errorMessage = error.message;
                                }
                                this.$swal({
                                    icon: 'error',
                                    title: 'Error!',
                                    text: errorMessage,
                                    customClass: {
                                        confirmButton: 'btn btn-primary',
                                    },
                                    showClass: {
                                        popup: 'animate__animated animate__shakeX',
                                    },
                                    buttonsStyling: false,
                                })
                                reject(e);
                            })
                    } else {
                        reject();
                    }
                })
            })
        },

        safeUseApplicableChange(isApplicable) {
            ProductProvider(this.getProductId()).isSafeUseApplicable = isApplicable;
            this.safeUseApplicable = isApplicable;
            this.updateProductState();
        },

        addDoC(document) {
            this.declarationsOfConformity.push(document);
        },

        docChange() {
            this.updateProductState();
        },

        updateProductState() {
            productState = this.makeProductState();
        },

        removeDoC(document, index) {
            return new Promise((resolve, reject) => {
                if (document.id) {
                    this.deleteDocument(document).then(() => {
                        this.declarationsOfConformity.splice(index, 1);
                        resolve();
                    }).catch(reject);
                } else {
                    this.declarationsOfConformity.splice(index, 1);
                    resolve();
                }
            });
        },

        addCompliantStatement(document) {
            this.compliantStatement.push(document);
        },

        compliantStatementChange() {
            this.updateProductState();
        },

        removeCompliantStatement(document, index) {
            return new Promise((resolve, reject) => {
                if (document.id) {
                    this.deleteDocument(document).then(() => {
                        this.compliantStatement.splice(index, 1);
                        resolve();
                    }).catch(reject);
                } else {
                    this.compliantStatement.splice(index, 1);
                    resolve();
                }
            });

        },

        addReport(report) {
            this.evidenceReports.push(report);
        },

        evidenceReportChange() {
            this.updateProductState();
        },

        removeReport(report, index) {
            return new Promise((resolve, reject) => {
                if (report.id) {
                    this.deleteDocument(report).then(() => {
                        this.evidenceReports.splice(index, 1);
                        resolve();
                    }).catch(reject);
                } else {
                    this.evidenceReports.splice(index, 1);
                    resolve();
                }
            });
        },

        onWeightChange(weight) {
            this.productCharacteristicsChange({weight: parseFloat(weight)});
        },

        productCharacteristicsChange(characteristics) {
            ProductProvider(this.getProductId()).setProductCharacteristics(characteristics);
        },

        productDescriptionChange(descriptionFile) {
            ProductProvider(this.getProductId()).setProductDescription(descriptionFile);
        },

        commentChange(comment) {
            ProductProvider(this.getProductId()).setComment(comment);
        },

        safeUseChange(instructions) {
            this.safeUseInstructions = instructions;
            ProductProvider(this.getProductId()).setSafeUseDocument(instructions);
        },

        disassemblingChange(instructions) {
            this.disassemblingInstructions = instructions;
            ProductProvider(this.getProductId()).setDissemblingDocument(instructions);
        },
        onSubstancePropertiesChange(substanceContainer, properties) {
            substanceContainer.properties = properties;
            this.needToSaveSubstances = true;
        },

        /**
         * @deprecated
         * @param substance
         * @param details
         */
        onSubstanceDetailsChange(substance, details) {
            this.needToSaveSubstances = true;
            // substance.configuration = details;
            // substance.details = details;
        },

        listQuestionStateUpdated(list, state) {
            ProductProvider(this.getProductId()).updateListQuestionState(list, state);
        },

        includeConcernSubstance(substance, substanceListIds = null) {
            this.needToSaveSubstances = true;
            const listIds = Array.isArray(substanceListIds) && substanceListIds.length > 0
                ? substanceListIds
                : this.legislations.map(l => l.substanceList.id);

            ProductProvider(this.getProductId()).includeSubstance(
                substance,
                listIds
            );
        },

        excludeConcernSubstance(substance) {
            this.needToSaveSubstances = true;
            ProductProvider(this.getProductId()).excludeSubstance(substance);
        },

        makeSubstanceUndefined(substance) {
            this.needToSaveSubstances = true;
            ProductProvider(this.getProductId()).makeSubstanceUndefined(substance);
        },

        hasConcernSubstances(value) {
            this.product.hasConcernSubstances = value;
        },

        //  Fetch

        fetchSubstancesAndDocuments() {
            Promise
                .all([
                    this.isFMDTabVisible ? this.fetchFMDDeclarations(true) : true,
                    this.fetchSubstances(),
                    this.fetchDocuments()
                ])
                .then(this.updateProductState);
        },

        fetchFMDDeclarations(refresh = false) {
            this.loadingFMDDeclarations = true;
            return ProductProvider(this.getProductId())
                .getFMDDeclarations(refresh)
                .then(declarations => {
                    this.fmdDeclarations = declarations;
                })
                .finally(() => this.loadingFMDDeclarations = false);
        },

        fetchSubstances(refresh = false) {
            return ProductProvider(this.getProductId())
                .getSubstances(refresh)
                .then(({included, excluded}) => {
                    this.includedSubstances = included;
                    this.excludedSubstances = excluded;
                });
        },

        fetchDocuments(refresh = false) {
            return ProductProvider(this.getProductId())
                .getDocuments(refresh)
                .then((documents) => {
                    this.declarationsOfConformity = documents.declarationOfConformityDocuments;
                    this.compliantStatement = documents.compliantStatementDocuments;
                    this.evidenceReports = documents.evidenceReportDocuments;
                    this.otherVerificationDocuments = documents.otherVerificationDocuments;
                    this.safeUseInstructions = documents.safeUseDocument;
                    this.disassemblingInstructions = documents.dissemblingDocument;
                    this.productDescription = documents.productDescription;
                });
        },

        //  Save
        save(showSuccessMessage = true, showToast = false, showSuccessToast = false, manageLoading = true) {
            if (manageLoading) {
                this.showLoading = true;
            }

            if (showToast) {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Notification',
                        icon: 'BellIcon',
                        text: 'Saving changes',
                        variant: 'info'
                    },
                });
            }

            let product = ProductProvider(this.getProductId());
            return product
                .save()
                .then(() => {
                    if (showSuccessMessage) {
                        this.$swal({
                            title: i18n.t('Component(ProductSavedAck):title'),
                            text: i18n.t('Component(ProductSavedAck):text'),
                            icon: 'success',
                            timer: 1300,
                            customClass: {
                                confirmButton: 'btn btn-primary',
                            },
                            buttonsStyling: false,
                        });
                    } else if (showSuccessToast) {
                        this.$toast({
                            component: ToastificationContent,
                            props: {
                                title: 'Notification',
                                icon: 'BellIcon',
                                text: 'Product has been successfully saved!',
                                variant: 'success'
                            },
                        });
                    }
                    this.statusExplanationRevision += 1;
                    this.refreshProduct();
                    this.updateProductState();
                    this.fetchSubstancesAndDocuments();
                })
                .catch((e) => {
                    backendResponse.methods.handleBackendError.call(this, e);
                })
                .finally(() => {
                    if (manageLoading) {
                        this.showLoading = false;
                    }
                });
        },

        remove() {
            return this.getProduct().remove().then(r => {
                clearCachedProduct(this.getProductId())
            });
        },
    },

    computed: {
        // Tabs
        isConcernElementsTabAccessable() {
            return false === this.isComplexProduct;
        },

        isComplexProduct() {
            return this.product.isComplex;
        },

        isProductHasConcernElements() {
            return this.includedSubstances.length > 0;
        },

        isVerificationTabVisible() {
            if (this.product.type === ProductType.FICTITIOUS) {
                return false;
            }
            return true;
            // return this.product.type !== ProductType.PURCHASED || this.product.isPutOnTheMarket !== false;
        },

        isFMDTabVisible() {
            if (!this.$store.getters.isFullMaterialDeclarationEnabled) {
                return false;
            }
            return this.product.type !== ProductType.FICTITIOUS;
        },

        isCompositionTabVisible() {
            if (this.isFMDTabVisible) {
                return false;
            }
            return this.$can('view', 'tab:product-card:composition') && this.product.type !== ProductType.FICTITIOUS;
        },

        isConcernElementsTabVisible() {
            if (this.$store.getters.isFullMaterialDeclarationEnabled) {
                return false;
            }
            return this.$can('view', 'tab:product-card:concern-elements') && this.product.type !== ProductType.FICTITIOUS;
        },

        isSupplierDocumentsTabVisible() {
            // if (this.product.type !== ProductType.PURCHASED) {
            //     return false;
            // }
            // return this.product.businessRoles.length === 0 || this.product.isPutOnTheMarket === false;
            return true;
        },
        isStatisticsTabVisible() {
            if (!this.$store.getters.organization.settings.statisticSettings) {
                return false;
            }
            if (!this.$store.getters.organization.settings.statisticSettings.showProductStatistics) {
                return false;
            }
            return this.product.type === ProductType.OWN && this.product.isTopLevel === true;
        },
        applicableLegislations() {
            const productLegislations = Array.isArray(this.product.legislations)
                ? this.product.legislations
                : Object.values(this.product.legislations || {})

            const applicableById = {};
            productLegislations.forEach((legislation) => {
                if (!legislation || legislation.id === undefined || legislation.id === null) {
                    return;
                }
                applicableById[String(legislation.id)] = legislation.isApplicable !== false;
            });

            return this.legislations.filter((legislation) => {
                if (!legislation || legislation.id === undefined || legislation.id === null) {
                    return false;
                }

                const id = String(legislation.id);
                if (!Object.prototype.hasOwnProperty.call(applicableById, id)) {
                    return true;
                }

                return applicableById[id];
            });
        }
    }
};
